import { X } from "@phosphor-icons/react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import type { ReactNode, RefObject } from "react";

import { focusRing } from "../../foundations/a11y";
import { cn } from "../../lib/cn";
import { motion as motionTokens } from "../../tokens";

export type ModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  dismissible?: boolean;
  footer?: ReactNode;
  className?: string;
  returnFocusRef?: RefObject<HTMLElement>;
  children: ReactNode;
};

export function Modal({
  open,
  onOpenChange,
  title,
  description,
  dismissible = true,
  footer,
  className,
  returnFocusRef,
  children,
}: ModalProps) {
  const reduce = useReducedMotion();

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <AnimatePresence>
        {open && (
          <DialogPrimitive.Portal forceMount>
            <DialogPrimitive.Overlay asChild forceMount>
              <motion.div
                className="bg-scrim/45 supports-[backdrop-filter]:bg-scrim/20 fixed inset-0 z-[60] backdrop-blur-md"
                initial={reduce ? false : { opacity: 0 }}
                animate={reduce ? undefined : { opacity: 1 }}
                exit={reduce ? undefined : { opacity: 0 }}
                transition={{ duration: 0.2, ease: "easeOut" }}
              />
            </DialogPrimitive.Overlay>
            <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
              <DialogPrimitive.Content
                asChild
                forceMount
                onCloseAutoFocus={(event) => {
                  if (returnFocusRef?.current?.isConnected) {
                    event.preventDefault();
                    returnFocusRef.current.focus();
                  }
                }}
                onEscapeKeyDown={(event) => {
                  if (!dismissible) event.preventDefault();
                }}
                onPointerDownOutside={(event) => {
                  if (!dismissible) event.preventDefault();
                }}
                {...(description ? {} : { "aria-describedby": undefined })}
              >
                <motion.div
                  className={cn(
                    "bg-surface text-text flex max-h-[calc(100dvh-2rem)] w-full max-w-md flex-col rounded-xl shadow-modal",
                    className,
                  )}
                  initial={reduce ? false : { opacity: 0, scale: 0.96, y: 8 }}
                  animate={reduce ? undefined : { opacity: 1, scale: 1, y: 0 }}
                  exit={reduce ? undefined : { opacity: 0, scale: 0.96, y: 8 }}
                  transition={motionTokens.spring.default}
                >
                  <div className="flex items-start justify-between gap-3 px-5 pb-2 pt-5">
                    <div className="flex flex-col gap-1">
                      <DialogPrimitive.Title className="text-heading text-text">
                        {title}
                      </DialogPrimitive.Title>
                      {description && (
                        <DialogPrimitive.Description className="text-body text-text-secondary">
                          {description}
                        </DialogPrimitive.Description>
                      )}
                    </div>
                    {dismissible && (
                      <DialogPrimitive.Close
                        aria-label="Close"
                        className={cn(
                          "text-text-tertiary hover:bg-fill shrink-0 rounded-md p-1",
                          focusRing,
                        )}
                      >
                        <X size={20} aria-hidden="true" />
                      </DialogPrimitive.Close>
                    )}
                  </div>
                  <div className="min-h-0 overflow-y-auto px-5 py-2">
                    {children}
                  </div>
                  {footer && (
                    <div className="flex items-center justify-end gap-2 px-5 pb-5 pt-3">
                      {footer}
                    </div>
                  )}
                </motion.div>
              </DialogPrimitive.Content>
            </div>
          </DialogPrimitive.Portal>
        )}
      </AnimatePresence>
    </DialogPrimitive.Root>
  );
}
